/**
 * Agile Insights Dashboard
 * Displays Velocity and Planned vs Unplanned charts
 */

import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchVelocityData,
  fetchPlannedVsUnplannedData,
  fetchSprintInsights,
} from '../../store/sprintSlice';
import './AgileInsights.scss';

interface AgileInsightsProps {
  boardId: string;
  sprintId?: string;
}

const AgileInsights: React.FC<AgileInsightsProps> = ({ boardId, sprintId }) => {
  const dispatch = useAppDispatch();
  const { velocityData, plannedVsUnplannedData, insights } = useAppSelector(
    (state) => state.sprints
  );
  const [velocityView, setVelocityView] = React.useState<'bar' | 'line'>('bar');

  useEffect(() => {
    if (boardId) {
      dispatch(fetchVelocityData(boardId));
    }
    if (sprintId) {
      dispatch(fetchPlannedVsUnplannedData(sprintId));
      dispatch(fetchSprintInsights(sprintId));
    }
  }, [boardId, sprintId, dispatch]);

  const formattedVelocityData = useMemo(() => {
    if (!velocityData || !Array.isArray(velocityData)) return [];
    return velocityData.map((v: any) => ({
      ...v,
      sprintName: v.sprintName || `Sprint ${v.sprintId}`,
      completed: v.completed !== undefined ? v.completed : (v.completedPoints || 0),
      committed: v.committed !== undefined ? v.committed : (v.committedPoints || v.completedPoints || 0),
    }));
  }, [velocityData]);

  const handleVelocityViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: 'bar' | 'line' | null
  ) => {
    if (newView !== null) {
      setVelocityView(newView);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className="custom-tooltip">
          <Typography variant="subtitle2" className="tooltip-label">
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} className="tooltip-entry">
              <Box
                className="tooltip-dot"
                sx={{ backgroundColor: entry.color }}
              />
              <Typography variant="body2">
                {entry.name}: <strong>{entry.value}</strong> points
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const COLORS = {
    planned: '#2196f3',
    unplanned: '#ff9800',
    completed: '#4caf50',
    committed: '#2196f3',
  };

  const pieChartData = useMemo(() => {
    if (!plannedVsUnplannedData) return [];
    const data: any = plannedVsUnplannedData;
    if (data.sprints && Array.isArray(data.sprints)) {
      return data.sprints.reduce(
        (acc: Array<{ name: string; value: number }>, sprint: any) => {
          acc[0].value += sprint.planned || sprint.plannedPoints || 0;
          acc[1].value += sprint.unplanned || sprint.unplannedPoints || 0;
          return acc;
        },
        [
          { name: 'Planned', value: 0 },
          { name: 'Unplanned', value: 0 },
        ]
      );
    }
    return [
      { name: 'Planned', value: data.plannedPoints ?? data.plannedCount ?? data.plannedStoryPoints ?? data.plannedTasks ?? 0 },
      { name: 'Unplanned', value: data.unplannedPoints ?? data.unplannedCount ?? data.unplannedStoryPoints ?? data.unplannedTasks ?? 0 },
    ];
  }, [plannedVsUnplannedData]);

  const PIE_COLORS = ['#2196f3', '#ff9800'];

  return (
    <Box className="agile-insights">
      <Box className="insights-header">
        <Box className="header-left">
          <TrendingUpIcon color="primary" fontSize="large" />
          <Typography variant="h5" className="header-title">
            Agile Insights
          </Typography>
        </Box>
        {insights && (
          <Box className="insights-summary">
            <Chip
              label={`Avg Velocity: ${insights.averageVelocity} pts`}
              color="primary"
              className="metric-chip"
            />
            <Chip
              label={`Predictability: ${insights.predictability || 0}%`}
              color={(insights.predictability || 0) >= 80 ? 'success' : 'warning'}
              className="metric-chip"
            />
          </Box>
        )}
      </Box>

      <Stack direction="row" flexWrap="wrap" spacing={3}>
        {/* Velocity Chart */}
        <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 calc(50% - 12px)" }, minWidth: 0 }}>
          <Card className="insight-card">
            <CardContent>
              <Box className="card-header">
                <Box className="header-left">
                  <ShowChartIcon color="primary" />
                  <Typography variant="h6">Sprint Velocity</Typography>
                </Box>
                <ToggleButtonGroup
                  value={velocityView}
                  exclusive
                  onChange={handleVelocityViewChange}
                  size="small"
                  className="view-toggle"
                >
                  <ToggleButton value="bar">Bar</ToggleButton>
                  <ToggleButton value="line">Line</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography variant="body2" color="text.secondary" className="chart-description">
                Track your team's velocity over time to improve sprint planning accuracy
              </Typography>

              <Box className="chart-container">
                <ResponsiveContainer width="100%" height={350}>
                  {velocityView === 'bar' ? (
                    <BarChart data={formattedVelocityData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="sprintName"
                        stroke="#5e6c84"
                        style={{ fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#5e6c84"
                        style={{ fontSize: 12 }}
                        label={{
                          value: 'Story Points',
                          angle: -90,
                          position: 'insideLeft',
                          style: { fontSize: 12, fill: '#5e6c84' },
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 14 }}
                        iconType="circle"
                      />
                      <Bar
                        dataKey="committed"
                        fill={COLORS.committed}
                        name="Committed"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="completed"
                        fill={COLORS.completed}
                        name="Completed"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  ) : (
                    <LineChart data={formattedVelocityData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="sprintName"
                        stroke="#5e6c84"
                        style={{ fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#5e6c84"
                        style={{ fontSize: 12 }}
                        label={{
                          value: 'Story Points',
                          angle: -90,
                          position: 'insideLeft',
                          style: { fontSize: 12, fill: '#5e6c84' },
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 14 }}
                        iconType="line"
                      />
                      <Line
                        type="monotone"
                        dataKey="committed"
                        stroke={COLORS.committed}
                        strokeWidth={3}
                        dot={{ r: 5 }}
                        name="Committed"
                      />
                      <Line
                        type="monotone"
                        dataKey="completed"
                        stroke={COLORS.completed}
                        strokeWidth={3}
                        dot={{ r: 5 }}
                        name="Completed"
                      />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </Box>

              {/* Velocity Metrics */}
              {insights && (
                <Box className="metrics-footer">
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Average Velocity
                    </Typography>
                    <Typography variant="h6" className="metric-value">
                      {insights.averageVelocity} pts
                    </Typography>
                  </Box>
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Best Sprint
                    </Typography>
                    <Typography variant="h6" className="metric-value">
                      {insights.bestVelocity || 0} pts
                    </Typography>
                  </Box>
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Completion Rate
                    </Typography>
                    <Typography variant="h6" className="metric-value success">
                      {insights.completionRate || 0}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Planned vs Unplanned */}
        <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 calc(50% - 12px)" }, minWidth: 0 }}>
          <Card className="insight-card">
            <CardContent>
              <Box className="card-header">
                <Box className="header-left">
                  <DonutLargeIcon color="primary" />
                  <Typography variant="h6">Planned vs Unplanned</Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" className="chart-description">
                Monitor scope changes and unplanned work to maintain sprint stability
              </Typography>

              <Stack direction="row" flexWrap="wrap" spacing={2}>
                {/* Pie Chart */}
                <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
                  <Box className="pie-chart-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }: any) =>
                            `${name}: ${((percent as number) * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Bar Chart */}
                <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
                  <Box className="bar-chart-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart
                        data={plannedVsUnplannedData?.sprints}
                        layout="horizontal"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                          dataKey="sprintName"
                          stroke="#5e6c84"
                          style={{ fontSize: 11 }}
                        />
                        <YAxis stroke="#5e6c84" style={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="planned"
                          stackId="a"
                          fill={COLORS.planned}
                          name="Planned"
                        />
                        <Bar
                          dataKey="unplanned"
                          stackId="a"
                          fill={COLORS.unplanned}
                          name="Unplanned"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Stack>

              {/* Summary Metrics */}
              {plannedVsUnplannedData && (
                <Box className="metrics-footer">
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Total Planned
                    </Typography>
                    <Typography variant="h6" className="metric-value">
                      {pieChartData[0]?.value || 0} pts
                    </Typography>
                  </Box>
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Total Unplanned
                    </Typography>
                    <Typography variant="h6" className="metric-value warning">
                      {pieChartData[1]?.value || 0} pts
                    </Typography>
                  </Box>
                  <Box className="metric-item">
                    <Typography variant="caption" color="text.secondary">
                      Unplanned Ratio
                    </Typography>
                    <Typography variant="h6" className="metric-value">
                      {plannedVsUnplannedData.averageUnplannedRatio.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Key Insights Summary */}
        {insights && (
          <Box sx={{ flex: "1 1 100%", minWidth: 0 }}>
            <Card className="summary-card">
              <CardContent>
                <Typography variant="h6" className="summary-title">
                  Key Insights & Recommendations
                </Typography>
                <Stack direction="row" flexWrap="wrap" spacing={2} className="insights-grid">
                  <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 10.667px)" }, minWidth: 0 }}>
                    <Box className="insight-box">
                      <Typography variant="subtitle2" className="insight-label">
                        Sprint Consistency
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(insights.predictability || 0) >= 80
                          ? 'Excellent! Your team is consistently meeting commitments.'
                          : (insights.predictability || 0) >= 60
                          ? 'Good. Consider reviewing estimation practices.'
                          : 'Needs improvement. Review sprint planning and capacity.'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 10.667px)" }, minWidth: 0 }}>
                    <Box className="insight-box">
                      <Typography variant="subtitle2" className="insight-label">
                        Velocity Trend
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(insights.velocityTrend || 'stable') === 'increasing'
                          ? 'Velocity is increasing. Team efficiency is improving!'
                          : (insights.velocityTrend || 'stable') === 'stable'
                          ? 'Velocity is stable. Maintain current practices.'
                          : 'Velocity is decreasing. Investigate blockers or capacity issues.'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 10.667px)" }, minWidth: 0 }}>
                    <Box className="insight-box">
                      <Typography variant="subtitle2" className="insight-label">
                        Scope Management
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plannedVsUnplannedData &&
                        plannedVsUnplannedData.averageUnplannedRatio < 20
                          ? 'Great scope control. Minimal unplanned work.'
                          : plannedVsUnplannedData &&
                            plannedVsUnplannedData.averageUnplannedRatio < 40
                          ? 'Moderate scope changes. Monitor closely.'
                          : 'High unplanned work. Improve sprint planning and buffer.'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default AgileInsights;
